import axiosInstance from '@/api/axiosInstance';
import { SITE_MENU_ENDPOINTS } from '@/api/endpoints/site-menu.endpoints';
import { sharedRequest } from '@/utils/sharedRequest';

export type VisibilityType = 'role' | 'cohort';

export interface VisibilityRule {
  id?: number;
  visibility_type: VisibilityType;
  target_id: number;
}

export interface SiteMenuItem {
  id: number;
  tenant_id: number | null;
  label: string;
  icon: string | null;
  /** null/empty = a PARENT (group) entry — not a link, expands its children. */
  url: string | null;
  tooltip: string | null;
  sort_order: number;
  is_active: boolean;
  open_in_new_tab: boolean;
  parent_id?: number | null;
  is_parent?: boolean;
  created_at: number;
  updated_at: number;
  visibility_rules: VisibilityRule[];
}

export interface SiteMenuItemCreate {
  label: string;
  icon?: string | null;
  /** Omit/null for a parent (group) entry. */
  url?: string | null;
  tooltip?: string | null;
  sort_order?: number;
  is_active?: boolean;
  open_in_new_tab?: boolean;
  parent_id?: number | null;
  visibility_rules?: VisibilityRule[];
}

export type SiteMenuItemUpdate = Partial<SiteMenuItemCreate> & {
  visibility_rules?: VisibilityRule[];
  /** Explicitly detach from the current parent. */
  clear_parent?: boolean;
  /** Explicitly turn a link into a parent (group) entry. */
  clear_url?: boolean;
};

export interface SiteMenuListResponse {
  count: number;
  results: SiteMenuItem[];
}

// Labels/tooltips are multilang strings that the ADMIN editor must round-trip
// and the SIDEBAR parses per-locale (useMultilang) — the global axios
// interceptor would flatten them to English, so these requests opt out.
const RAW = { skipMultilangStrip: true } as object;

export const SiteMenuService = {
  list: async (): Promise<SiteMenuListResponse> =>
    (await axiosInstance.get<SiteMenuListResponse>(SITE_MENU_ENDPOINTS.LIST, RAW)).data,

  create: async (data: SiteMenuItemCreate): Promise<SiteMenuItem> =>
    (await axiosInstance.post<SiteMenuItem>(SITE_MENU_ENDPOINTS.CREATE, data, RAW)).data,

  get: async (id: number): Promise<SiteMenuItem> =>
    (await axiosInstance.get<SiteMenuItem>(SITE_MENU_ENDPOINTS.DETAIL(id), RAW)).data,

  update: async (id: number, data: SiteMenuItemUpdate): Promise<SiteMenuItem> =>
    (await axiosInstance.put<SiteMenuItem>(SITE_MENU_ENDPOINTS.UPDATE(id), data, RAW)).data,

  delete: async (id: number): Promise<void> =>
    (await axiosInstance.delete(SITE_MENU_ENDPOINTS.DELETE(id))).data,

  reorder: async (orderedIds: number[]): Promise<void> =>
    (await axiosInstance.post(SITE_MENU_ENDPOINTS.REORDER, { ordered_ids: orderedIds })).data,

  // Sidebar, TopBar and GlobalSearch all ask for this on every page load —
  // share one in-flight request (see sharedRequest).
  getUserMenu: (): Promise<SiteMenuListResponse> =>
    sharedRequest('site-menu:user-menu', async () =>
      (await axiosInstance.get<SiteMenuListResponse>(SITE_MENU_ENDPOINTS.USER_MENU, RAW)).data,
    ),
};
